import { getDeviceInfo } from '../Upshow';
import SocketService from './SocketService';

export const LOGS_EVENT = 'device-logs';

export const TRANSPORT_HTTP = 'http';
export const TRANSPORT_SOCKET = 'socket';
export const TRANSPORT_SOCKET_DEGRADED = 'socket-degraded';

export const FLUSH_INTERVAL_MS = 10_000;
export const MAX_BATCH_LINES = 20;
// A frame over socket.io maxHttpBufferSize (1MB default) disconnects the client, which
// demotes the transport and can loop, so stay well under it.
export const MAX_FRAME_BYTES = 256 * 1024;
// Reserved out of the frame for the drop report, which is prepended after the batch is sized.
export const DROP_REPORT_BYTES = 128;
// A disconnect is usually a blip: hold the lines instead of racing them to HTTP, and
// only give up on the socket once this window expires. It has to outlast the manager's
// first retry (reconnectionDelay 30s randomised to [15s, 45s]) plus CONNECT_SETTLE_MS,
// or a socket-server deploy demotes half the fleet on a reconnect that would have worked.
export const DEGRADED_GRACE_MS = 60_000;
// Ceiling for a degraded device or a log storm outrunning the frame cap.
export const MAX_BUFFERED_LINES = 1_000;
// A connection has to hold this long before it owns logs: volatile.emit gives no delivery
// signal, so a flush into a socket that is about to drop loses the lines silently.
export const CONNECT_SETTLE_MS = 5_000;
export const FLAP_THRESHOLD = 3;
export const FLAP_WINDOW_MS = 120_000;
export const BREAKER_COOLDOWN_MS = 600_000;

// String.length counts UTF-16 units; a CJK or emoji line is up to 3x that on the wire, so
// counting characters is how a frame under the 256KB cap still trips the server's 1MB limit.
export function utf8Length (str) {
    let bytes = 0;

    for (let i = 0; i < str.length; i++) {
        const code = str.charCodeAt(i);

        if (code < 0x80) bytes += 1;
        else if (code < 0x800) bytes += 2;
        else if (code >= 0xd800 && code <= 0xdbff && i + 1 < str.length) { bytes += 4; i++; }
        else bytes += 3;
    }

    return bytes;
}

// The lines are not the whole frame: ts and deviceInfo ride along with them.
export function frameBudget (deviceInfo) {
    return MAX_FRAME_BYTES - utf8Length(JSON.stringify({ ts: Date.now(), deviceInfo, logs: [] })) - DROP_REPORT_BYTES;
}

// Set by the loader URL (index.js). Absent on an older loader, where logs stay on the socket.
export function logsSocketEnabled () {
    return window.logs_socket_enabled !== false;
}

export function buildDeviceInfo (info = {}) {
    return {
        version: info.version,
        uiVersion: info.uiVersion,
        model: info.model,
        serial: info.serial,
        sessionId: info.sessionId,
        deviceUptime: info.DeviceUptime
    };
}

export class DeviceLogsSocket {
    transport = TRANSPORT_HTTP;
    buffer = [];
    droppedLines = 0;

    socket = null;
    spillHandler = null;
    deviceInfoProvider = getDeviceInfo;

    flushTimer = null;
    graceTimer = null;
    settleTimer = null;
    breakerTimer = null;
    breakerOpen = false;
    flapCount = 0;
    flapTimers = [];
    listeners = null;

    register ({ socket = SocketService.socket, spillToHttp = null, deviceInfoProvider = getDeviceInfo } = {}) {
        this.unregister();

        this.spillHandler = spillToHttp;
        this.deviceInfoProvider = deviceInfoProvider;

        // Kill switch: logs go back to the HTTP path and nothing here observes the socket, which
        // keeps connecting for commands, heartbeat metrics and live content.
        if (!logsSocketEnabled()) return;

        this.socket = socket ?? null;

        if (!this.socket) return;

        this.listeners = {
            connect: () => this._onConnect(),
            disconnect: () => this._onDisconnect(),
            reconnect_failed: () => this._onReconnectFailed(),
            // A reload takes the buffer with it, and those lines are the ones that explain
            // the reload; the socket holds them for 10s where the HTTP path held them for 3s.
            pagehide: () => this.flush()
        };

        this.socket.on('connect', this.listeners.connect);
        this.socket.on('disconnect', this.listeners.disconnect);
        this.socket.on('reconnect_failed', this.listeners.reconnect_failed);
        // socket.io v4 emits reconnect_failed on the manager, not on the socket
        this.socket.io?.on?.('reconnect_failed', this.listeners.reconnect_failed);
        window.addEventListener('pagehide', this.listeners.pagehide);

        if (this.socket.connected) this._onConnect();
    }

    unregister () {
        if (this.socket && this.listeners) {
            this.socket.off?.('connect', this.listeners.connect);
            this.socket.off?.('disconnect', this.listeners.disconnect);
            this.socket.off?.('reconnect_failed', this.listeners.reconnect_failed);
            this.socket.io?.off?.('reconnect_failed', this.listeners.reconnect_failed);
            window.removeEventListener('pagehide', this.listeners.pagehide);
        }

        this._clearFlushTimer();
        this._clearGraceTimer();
        this._clearSettleTimer();
        this._clearBreaker();
        this.listeners = null;
        this.socket = null;
    }

    getTransport () {
        return this.transport;
    }

    getBufferedCount () {
        return this.buffer.length;
    }

    ownsLogs () {
        return this.transport !== TRANSPORT_HTTP;
    }

    push (line) {
        if (!this.ownsLogs()) return false;

        this.buffer.push(line);
        this._trimBuffer();

        if (this.buffer.length >= MAX_BATCH_LINES) {
            this.flush();
        } else if (this.transport === TRANSPORT_SOCKET) {
            this._startFlushTimer();
        }

        return true;
    }

    flush () {
        this._clearFlushTimer();

        if (this.transport !== TRANSPORT_SOCKET || !this.socket?.connected) return;

        // socket.io discards a volatile packet while the transport is mid-write, a state
        // `connected` does not express, and _takeBatch() empties the buffer before the emit.
        // Only an engine that says so blocks a flush; an unknown shape is assumed writable.
        if (this.socket.io?.engine?.transport?.writable === false) {
            if (this.buffer.length > 0) this._startFlushTimer();
            return;
        }

        const deviceInfo = buildDeviceInfo(this.deviceInfoProvider?.());
        const logs = this._takeBatch(frameBudget(deviceInfo));

        // Counted, not taken: a report zeroed into a frame that never leaves loses the loss.
        const dropped = this.droppedLines;
        if (dropped > 0) logs.unshift(this._dropReport(dropped));

        if (logs.length > 0) {
            try {
                // volatile: a plain emit would sit in socket.io own offline queue and replay
                // on reconnect the lines the grace window already spilled to HTTP.
                this.socket.volatile.emit(LOGS_EVENT, { ts: Date.now(), deviceInfo, logs });
            } catch (e) {
                if (dropped > 0) logs.shift();
                this.buffer = logs.concat(this.buffer);
                this._trimBuffer();
                this._startFlushTimer();
                return;
            }

            this.droppedLines -= dropped;
        }

        if (this.buffer.length > 0) this._startFlushTimer();
    }

    _trimBuffer () {
        const overflow = this.buffer.length - MAX_BUFFERED_LINES;
        if (overflow > 0) {
            this.buffer.splice(0, overflow);
            this.droppedLines += overflow;
        }
    }

    _dropReport (count) {
        return {
            name: 'event.device_logs_dropped',
            ts: Date.now(),
            stuff: { count }
        };
    }

    _takeDropReport () {
        if (this.droppedLines === 0) return null;

        const report = this._dropReport(this.droppedLines);
        this.droppedLines = 0;

        return report;
    }

    _takeBatch (budget) {
        const logs = [];
        let bytes = 2;

        while (this.buffer.length > 0) {
            const { name, ts, stuff } = this.buffer[0];
            const line = { name, ts, stuff };
            let size;

            try {
                size = utf8Length(JSON.stringify(line)) + 1;
            } catch (e) {
                // An unserializable line would throw at the logger writer that produced it.
                size = Infinity;
            }

            if (size > budget) {
                this.buffer.shift();
                this.droppedLines++;
                continue;
            }

            if (bytes + size > budget) break;

            bytes += size;
            logs.push(line);
            this.buffer.shift();
        }

        return logs;
    }

    _onConnect () {
        if (this.breakerOpen) return;
        this._startSettleTimer();
    }

    _onDisconnect () {
        this._clearSettleTimer();

        if (this.breakerOpen) return;

        this._recordFlap();

        // The grace deadline is armed on the socket -> degraded edge only. A reconnect that
        // never survives the settle period leaves the transport degraded, so it cannot push
        // the deadline out and the fallback still trips on schedule.
        if (this.transport === TRANSPORT_SOCKET) {
            this.transport = TRANSPORT_SOCKET_DEGRADED;
            this._clearFlushTimer();
            this.graceTimer = setTimeout(() => this._fallBackToHttp(), DEGRADED_GRACE_MS);
        }

        if (this.flapCount >= FLAP_THRESHOLD) this._tripBreaker();
    }

    _onReconnectFailed () {
        this._fallBackToHttp();
    }

    _promote () {
        this._clearGraceTimer();
        this.transport = TRANSPORT_SOCKET;
        if (this.buffer.length > 0) this._startFlushTimer();
    }

    _tripBreaker () {
        this._clearFlaps();
        this._clearSettleTimer();

        this.breakerOpen = true;
        this.breakerTimer = setTimeout(() => {
            this.breakerTimer = null;
            this.breakerOpen = false;
            // No further 'connect' will fire on a socket that stabilised during the cooldown.
            if (this.socket?.connected) this._startSettleTimer();
        }, BREAKER_COOLDOWN_MS);

        this._fallBackToHttp();
    }

    _recordFlap () {
        this.flapCount++;

        const timer = setTimeout(() => {
            this.flapTimers = this.flapTimers.filter(t => t !== timer);
            this.flapCount--;
        }, FLAP_WINDOW_MS);

        this.flapTimers.push(timer);
    }

    _clearFlaps () {
        this.flapTimers.forEach(clearTimeout);
        this.flapTimers = [];
        this.flapCount = 0;
    }

    _clearBreaker () {
        this._clearFlaps();
        if (this.breakerTimer) clearTimeout(this.breakerTimer);
        this.breakerTimer = null;
        this.breakerOpen = false;
    }

    _fallBackToHttp () {
        this._clearGraceTimer();
        this._clearFlushTimer();
        this.transport = TRANSPORT_HTTP;

        const spilled = this.buffer;
        this.buffer = [];

        const dropped = this._takeDropReport();
        if (dropped) spilled.push(dropped);

        if (spilled.length > 0 && this.spillHandler) this.spillHandler(spilled);
    }

    _startSettleTimer () {
        if (this.settleTimer) return;
        this.settleTimer = setTimeout(() => {
            this.settleTimer = null;
            if (!this.breakerOpen) this._promote();
        }, CONNECT_SETTLE_MS);
    }

    _clearSettleTimer () {
        if (!this.settleTimer) return;
        clearTimeout(this.settleTimer);
        this.settleTimer = null;
    }

    _startFlushTimer () {
        if (this.flushTimer) return;
        this.flushTimer = setTimeout(() => {
            this.flushTimer = null;
            this.flush();
        }, FLUSH_INTERVAL_MS);
    }

    _clearFlushTimer () {
        if (!this.flushTimer) return;
        clearTimeout(this.flushTimer);
        this.flushTimer = null;
    }

    _clearGraceTimer () {
        if (!this.graceTimer) return;
        clearTimeout(this.graceTimer);
        this.graceTimer = null;
    }
}

export default new DeviceLogsSocket();
